import { useMemo } from 'react'
import { useGetOtuProcessingDataSelector } from '../../../../../Flux/Selector/index.js'
import type { IOrder } from '../../../../../Model/index.js'
import { CollapsibleSpan } from '../PipelineInformationModal.js'
import { formatDate, parseDate } from '../../../../../Util/DateHandling.ts'
import { getResultUnitLabel, isEdnaProcessingType, isOtuProcessingType } from '../../../../../Util/processingType.ts'
import { useDispatch } from 'react-redux'
import Actions from '../../../../../Flux/index.js'
import { orderSlice } from '../../../../../Flux/Slice/Order/OrderSlice.js'
import { FLAG_METADATA } from './otuFlags.js'

interface OtuProcessingTabProps {
  order: IOrder
  onClose: () => void
}

const renderDate = (date?: string) => {
  if (!date) return 'N/A'
  return formatDate(parseDate(date))
}

type ArtifactInput = {
  id: string
  stableKey?: string
  name?: string
}

type ArtifactRow = {
  artifact: {
    id: string
    name?: string
    codeVersion?: string
    publishedAt?: string
  }
  inputs?: ArtifactInput[]
}

function capitalizeFirstLetter(val: string): string {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1)
}

const getReferenceRowName = (item: ArtifactRow) => {
  const artifactName = item.artifact.name?.trim()
  const inputNames = (item.inputs ?? []).map((input) => input.name?.trim()).filter(Boolean)

  if (artifactName) {
    if (inputNames.includes('midori2')) {
      return 'Midori 2 RDP classifier'
    }
    if (inputNames.includes('bold')) {
      return (
        'BOLD (' +
        artifactName
          .split('_')
          .map((x) => capitalizeFirstLetter(x))
          .join(' ') +
        ')'
      )
    }
    if (inputNames.includes('ncbi-nt')) {
      return 'NCBI nt (' + artifactName + ')'
    }
  }

  // fallback
  return artifactName ?? 'N/A'
}

export const OtuProcessingTab = (props: OtuProcessingTabProps) => {
  const otuProcessingData = useGetOtuProcessingDataSelector()
  const dispatch = useDispatch()

  const flagEntries = Object.entries(otuProcessingData?.flags ?? {})
  const versionItems = otuProcessingData?.versions.items ?? []

  const reportTimestamp = useMemo(() => {
    const publishedDates = versionItems.map((item) => item.artifact.publishedAt).filter((date): date is string => Boolean(date))

    if (!publishedDates.length) return undefined

    return [...publishedDates].sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
  }, [versionItems])

  const latestPublishedLabel = useMemo(() => {
    if (!reportTimestamp) return 'N/A'
    return renderDate(reportTimestamp)
  }, [reportTimestamp])

  return (
    <>
      <input
        type="radio"
        name="my_tabs_1"
        role="tab"
        className="tab"
        aria-label={`${getResultUnitLabel(props.order.processingType)} Processing`}
      />
      <div role="tabpanel" className="tab-content p-6 md:p-8">
        <div className="space-y-6">
          <div className="stats stats-vertical lg:stats-horizontal w-full border border-base-300 bg-base-100 shadow-sm">
            <div className="stat">
              <div className="stat-title">Reference Sets</div>
              <div className="stat-value text-base md:text-lg">{versionItems.length}</div>
              <div className="stat-desc">Databases included in this run</div>
            </div>

            <div className="stat">
              <div className="stat-title">Last Published</div>
              <div className="stat-value text-base md:text-lg">{latestPublishedLabel}</div>
              <div className="stat-desc">Formatted display value</div>
            </div>
          </div>

          <div className="rounded-box border border-base-300 bg-base-100 shadow-sm">
            <div className="border-b border-base-300 px-4 py-3">
              <h3 className="text-base font-semibold">Run Details</h3>
              <p className="text-sm text-base-content/70">
                Key values captured during {getResultUnitLabel(props.order.processingType)} processing.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {flagEntries.length > 0 ? (
                    flagEntries.map(([key, value]) => {
                      const meta = FLAG_METADATA[key]

                      return (
                        <tr key={key}>
                          <td className="align-top">
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">{meta?.label ?? key}</span>
                              {meta?.description && (
                                <span className="text-xs leading-relaxed text-base-content/70">{meta.description}</span>
                              )}
                            </div>
                          </td>
                          <td className="align-top">{meta?.format ? meta.format(String(value)) : String(value)}</td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={2}>No run details available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-box border border-base-300 bg-base-100 shadow-sm">
            <div className="border-b border-base-300 px-4 py-3">
              <h3 className="text-base font-semibold">Reference Databases Used</h3>
              <p className="text-sm text-base-content/70">Source datasets and fingerprints used to generate this report.</p>
            </div>

            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Version</th>
                    <th>Published</th>
                    <th>Input Fingerprint</th>
                  </tr>
                </thead>
                <tbody>
                  {versionItems.length ? (
                    versionItems.map((item) => (
                      <tr key={item.artifact.id}>
                        <td className="align-top">
                          <div className="font-medium">{getReferenceRowName(item)}</div>
                        </td>
                        <td className="align-top">{item.artifact.codeVersion ?? 'N/A'}</td>
                        <td className="align-top">{renderDate(item.artifact.publishedAt)}</td>
                        <td className="align-top">
                          {item.inputs?.length ? (
                            <div className="flex flex-col gap-2">
                              {item.inputs.map((input) => (
                                <div key={input.id} className="flex flex-col gap-1">
                                  {input.name && (
                                    <span className="text-xs font-medium uppercase tracking-wide text-base-content/60">{input.name}</span>
                                  )}
                                  <CollapsibleSpan maxChars={40} content={input.stableKey ?? String(input.id)} />
                                </div>
                              ))}
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>No reference database versions available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="modal-action mt-6">
          {props.order.orderDone && isOtuProcessingType(props.order.processingType) && (
            <button className="btn btn-outline" onClick={() => window.open(`/order/${props.order.id}/print-report`, '_blank')}>
              Printable Preview
            </button>
          )}
          {props.order.orderDone && isEdnaProcessingType(props.order.processingType) && (
            <button
              className="btn btn-outline"
              onClick={() => dispatch(
                Actions[orderSlice.name].downloadOrderArtifactRequest({ orderId: props.order.id, artifact: 'PROCESSING_SUMMARY' })
              )}
            >
              Report Summary
            </button>
          )}
          <button className="btn btn-primary" onClick={props.onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  )
}
